/**
 * VT Compare — standalone product comparison system for Shopify Horizon 4.0.
 *
 * Vanilla ES module web component. No jQuery. No theme dependencies.
 * Persists the compare list in localStorage under the key "vt_compare" as a
 * JSON array of product handles. Maximum of 4 products.
 *
 * Public API (named export):
 *   VTCompare.add(handle)     -> boolean  (true if added, false if capped/exists)
 *   VTCompare.remove(handle)  -> boolean  (true if removed)
 *   VTCompare.has(handle)     -> boolean
 *   VTCompare.getAll()        -> string[] (handles, insertion order)
 *   VTCompare.getCount()      -> number
 *
 * Custom elements:
 *   <vt-compare-button>  — toggle button (product cards / PDP)
 *   <vt-compare-page>    — renders the full comparison table
 *
 * Events (dispatched on window):
 *   "compare:updated"  — { detail: { handles: string[], count: number } }
 *
 * Usage in a section:
 *   <script type="module" src="{{ 'vt-compare.js' | asset_url }}"></script>
 *   <vt-compare-page></vt-compare-page>
 */

const STORAGE_KEY = 'vt_compare';
const MAX_PRODUCTS = 4;

/* ------------------------------------------------------------------ */
/* VTCompare namespace                                                 */
/* ------------------------------------------------------------------ */

function readHandles() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.filter((h) => typeof h === 'string' && h) : [];
  } catch (_err) {
    return [];
  }
}

function writeHandles(handles) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(handles));
  } catch (_err) {
    /* storage may be unavailable (private mode) — fail silently */
  }
}

function dispatchUpdated() {
  const handles = readHandles();
  window.dispatchEvent(
    new CustomEvent('compare:updated', {
      detail: { handles, count: handles.length },
    })
  );
}

export const VTCompare = {
  /** Add a product handle to the compare list. Returns true if added. */
  add(handle) {
    if (!handle) return false;
    const handles = readHandles();
    if (handles.includes(handle)) return false;
    if (handles.length >= MAX_PRODUCTS) return false;
    handles.push(handle);
    writeHandles(handles);
    dispatchUpdated();
    return true;
  },

  /** Remove a product handle. Returns true if it was present. */
  remove(handle) {
    if (!handle) return false;
    const handles = readHandles();
    const idx = handles.indexOf(handle);
    if (idx === -1) return false;
    handles.splice(idx, 1);
    writeHandles(handles);
    dispatchUpdated();
    return true;
  },

  /** Toggle a handle in the list. Returns the new "has" state. */
  toggle(handle) {
    if (this.has(handle)) {
      this.remove(handle);
      return false;
    }
    return this.add(handle);
  },

  /** Whether the list is full and cannot accept another product. */
  isFull() {
    return readHandles().length >= MAX_PRODUCTS;
  },

  /** Whether a handle is currently in the compare list. */
  has(handle) {
    return readHandles().includes(handle);
  },

  /** All handles in insertion order. */
  getAll() {
    return readHandles();
  },

  /** Current number of products in the compare list. */
  getCount() {
    return readHandles().length;
  },

  /** The configured maximum. */
  max: MAX_PRODUCTS,

  /** The localStorage key in use. */
  storageKey: STORAGE_KEY,
};

/* ------------------------------------------------------------------ */
/* Money formatting (defensive — no theme dependency)                  */
/* ------------------------------------------------------------------ */

/**
 * Format a cents value into a price string using the shop's money format when
 * available. Falls back to a sensible "$0.00" with the active currency code.
 * Supports the common Shopify money format tokens.
 */
function formatMoney(cents, format) {
  if (typeof cents === 'string') cents = cents.replace('.', '');
  cents = Number(cents || 0);
  if (isNaN(cents)) cents = 0;

  let fmt = format || '';
  if (!fmt) {
    fmt =
      (window.Shopify && window.Shopify.money_format) ||
      (window.theme && window.theme.moneyFormat) ||
      (window.money_format) ||
      '${{amount}}';
  }

  const currency = (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || '';

  function defaultDecimals(f) {
    return f.indexOf('no_decimals') > -1 ? 0 : 2;
  }

  function formatWithDelimiters(number, precision, thousands, decimal) {
    precision = precision == null ? 2 : precision;
    thousands = thousands || ',';
    decimal = decimal || '.';

    if (isNaN(number) || number == null) number = 0;
    const fixed = (number / 100).toFixed(precision);
    const [intPart, decPart] = fixed.split('.');
    let withThousands = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
    return decPart ? `${withThousands}${decimal}${decPart}` : withThousands;
  }

  // Placeholder -> rendered value map (in order of specificity)
  const dec = defaultDecimals(fmt);
  const value = formatWithDelimiters(cents, dec);
  const valueNoDecimals = formatWithDelimiters(cents, 0);
  const valueComma = formatWithDelimiters(cents, dec, '.', ',');
  const valueCommaNoDecimals = formatWithDelimiters(cents, 0, '.', ',');
  const valueApostrophe = formatWithDelimiters(cents, dec, "'", '.');

  return fmt
    .replace('{{amount_with_currency}}', `${value} ${currency}`.trim())
    .replace('{{amount_no_decimals_with_comma_separator}}', valueCommaNoDecimals)
    .replace('{{amount_no_decimals}}', valueNoDecimals)
    .replace('{{amount_with_comma_separator}}', valueComma)
    .replace('{{amount_with_apostrophe_separator}}', valueApostrophe)
    .replace('{{amount}}', value)
    .trim();
}

function formatPrice(price) {
  return formatMoney(price);
}

/* ------------------------------------------------------------------ */
/* Fetching product data                                               */
/* ------------------------------------------------------------------ */

const productCache = new Map();

async function fetchProduct(handle) {
  if (!handle) return null;
  if (productCache.has(handle)) return productCache.get(handle);
  try {
    const res = await fetch(`/products/${encodeURIComponent(handle)}.js`, {
      headers: { Accept: 'application/json' },
    });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const data = await res.json();
    productCache.set(handle, data);
    return data;
  } catch (err) {
    // Cache the failure so we don't hammer the API on re-render.
    productCache.set(handle, null);
    return null;
  }
}

/* ------------------------------------------------------------------ */
/* HTML helpers                                                        */
/* ------------------------------------------------------------------ */

function escapeHtml(str) {
  if (str == null) return '';
  return String(str)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

function featuredImage(product) {
  const img = product.featured_image || (product.images && product.images[0]);
  if (!img) return '';
  const src = typeof img === 'string' ? img : img.url || img.src || '';
  if (!src) return '';
  return src.replace(/(\.[a-zA-Z0-9]+)(\?|$)/, '_200x$1$2');
}

/* ------------------------------------------------------------------ */
/* vt-compare-button                                                   */
/* ------------------------------------------------------------------ */

const ICON_SVG = `
<svg class="vt-compare-btn__icon" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false">
  <path d="M16 3h5v5"></path>
  <path d="M8 3H3v5"></path>
  <path d="M21 3l-7 7"></path>
  <path d="M3 3l7 7"></path>
  <path d="M12 21V11"></path>
  <path d="M8 21h8"></path>
</svg>`;

class VTCompareButton extends HTMLElement {
  constructor() {
    super();
    this._handle = this.getAttribute('data-handle') || '';
    this._onUpdated = this._syncState.bind(this);
  }

  connectedCallback() {
    if (!this._handle) {
      this._handle = this.getAttribute('data-handle') || '';
    }
    if (!this.querySelector('.vt-compare-btn__icon')) {
      this.innerHTML = ICON_SVG;
    }
    this.setAttribute('role', 'button');
    this.setAttribute('tabindex', '0');
    this.setAttribute('aria-pressed', 'false');
    this.setAttribute('aria-label', 'Compare this product');

    if (!this.hasAttribute('type')) this.setAttribute('type', 'button');

    this.removeEventListener('click', this._handleClick);
    this.removeEventListener('keydown', this._handleKeydown);
    this.addEventListener('click', this._handleClick);
    this.addEventListener('keydown', this._handleKeydown);

    window.addEventListener('compare:updated', this._onUpdated);
    this._syncState();
  }

  disconnectedCallback() {
    window.removeEventListener('compare:updated', this._onUpdated);
    this.removeEventListener('click', this._handleClick);
    this.removeEventListener('keydown', this._handleKeydown);
  }

  _handleClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    this._toggle();
  };

  _handleKeydown = (e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      e.stopPropagation();
      this._toggle();
    }
  };

  _toggle() {
    if (!this._handle) return;
    const had = VTCompare.has(this._handle);
    if (!had && VTCompare.isFull()) {
      // List is full and this product is not in it — alert the user.
      const msg =
        (window.theme && window.theme.strings && window.theme.strings.compareFull) ||
        `You can compare up to ${MAX_PRODUCTS} products at a time. Remove one to add another.`;
      // eslint-disable-next-line no-alert
      alert(msg);
      return;
    }
    VTCompare.toggle(this._handle);
    // _syncState will run via the compare:updated event, but toggle immediately too.
    this._syncState();
  }

  _syncState() {
    const active = VTCompare.has(this._handle);
    this.classList.toggle('is-active', active);
    this.setAttribute('aria-pressed', active ? 'true' : 'false');
    this.setAttribute('aria-label', active ? 'Remove from compare' : 'Compare this product');
  }
}

/* ------------------------------------------------------------------ */
/* vt-compare-page                                                     */
/* ------------------------------------------------------------------ */

class VTComparePage extends HTMLElement {
  constructor() {
    super();
    this._products = [];
    this._onUpdated = this._refresh.bind(this);
    this._abort = null;
  }

  connectedCallback() {
    this.setAttribute('role', 'region');
    this.setAttribute('aria-label', 'Product comparison');
    window.addEventListener('compare:updated', this._onUpdated);
    // Re-render when returning to the tab (localStorage may have changed elsewhere).
    window.addEventListener('storage', this._onStorage);
    this._render(); // initial render (will show empty state or table)
  }

  disconnectedCallback() {
    window.removeEventListener('compare:updated', this._onUpdated);
    window.removeEventListener('storage', this._onStorage);
    if (this._abort) this._abort.abort();
  }

  _onStorage = (e) => {
    if (e.key === STORAGE_KEY) this._refresh();
  };

  _refresh() {
    this._render();
  }

  async _render() {
    const handles = VTCompare.getAll();

    if (handles.length === 0) {
      this._renderEmpty();
      return;
    }

    // Show a loading skeleton while we fetch.
    this._renderLoading(handles.length);

    if (this._abort) this._abort.abort();
    this._abort = new AbortController();

    const results = await Promise.all(handles.map((h) => fetchProduct(h)));
    if (this._abort.signal.aborted) return;

    this._products = results;
    this._renderTable();
  }

  /* ---------- states ---------- */

  _renderEmpty() {
    this._products = [];
    const continueUrl =
      (window.theme && window.theme.routes && window.theme.routes.collections_url) ||
      '/collections';
    this.innerHTML = `
      <div class="vt-compare__empty">
        <div class="vt-compare__empty-icon" aria-hidden="true">
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">
            <path d="M16 3h5v5"></path>
            <path d="M8 3H3v5"></path>
            <path d="M21 3l-7 7"></path>
            <path d="M3 3l7 7"></path>
            <path d="M12 21V11"></path>
            <path d="M8 21h8"></path>
          </svg>
        </div>
        <h2 class="vt-compare__empty-title">Nothing to compare yet</h2>
        <p class="vt-compare__empty-text">Add products using the compare icon to see them side by side here.</p>
        <a class="vt-compare__empty-link" href="${escapeHtml(continueUrl)}">Continue shopping</a>
      </div>`;
  }

  _renderLoading(count) {
    const cells = Array.from({ length: count }, () => '').join('');
    this.innerHTML = `
      <div class="vt-compare__scroll" role="table" aria-busy="true">
        <table class="vt-compare__table vt-compare__table--loading">
          <tbody>
            <tr><th scope="row">Product</th>${cells.split('').map(() => '<td><span class="vt-compare__skeleton vt-compare__skeleton--title"></span></td>').join('')}</tr>
          </tbody>
        </table>
      </div>`;
  }

  _renderTable() {
    const products = this._products.filter(Boolean);

    // If every fetch failed, show empty + a note.
    if (products.length === 0) {
      this.innerHTML = `
        <div class="vt-compare__empty">
          <div class="vt-compare__empty-icon" aria-hidden="true">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">
              <circle cx="12" cy="12" r="10"></circle>
              <path d="M12 8v4"></path>
              <path d="M12 16h.01"></path>
            </svg>
          </div>
          <h2 class="vt-compare__empty-title">Nothing to compare yet</h2>
          <p class="vt-compare__empty-text">We couldn't load your saved products. They may no longer be available.</p>
          <a class="vt-compare__empty-link" href="/collections">Continue shopping</a>
        </div>`;
      return;
    }

    const rows = this._buildRows(products);
    this.innerHTML = `
      <div class="vt-compare__scroll">
        <table class="vt-compare__table">
          <caption class="visually-hidden">Product comparison</caption>
          <tbody>${rows}</tbody>
        </table>
      </div>
      <p class="vt-compare__hint" aria-live="polite">${products.length} of ${MAX_PRODUCTS} products.</p>`;
    this._wireTable();
  }

  _buildRows(products) {
    const headRow = `
      <tr class="vt-compare__row vt-compare__row--head">
        <th scope="col" class="vt-compare__label vt-compare__label--corner">Compare</th>
        ${products.map((p) => this._imageCell(p)).join('')}
      </tr>`;

    const titleRow = `
      <tr class="vt-compare__row">
        <th scope="row" class="vt-compare__label">Title</th>
        ${products.map((p) => `<td class="vt-compare__cell"><a class="vt-compare__title" href="${escapeHtml(p.url || '/products/' + p.handle)}">${escapeHtml(p.title)}</a></td>`).join('')}
      </tr>`;

    const priceRow = `
      <tr class="vt-compare__row">
        <th scope="row" class="vt-compare__label">Price</th>
        ${products.map((p) => `<td class="vt-compare__cell">${this._priceHtml(p)}</td>`).join('')}
      </tr>`;

    const vendorRow = `
      <tr class="vt-compare__row">
        <th scope="row" class="vt-compare__label">Vendor</th>
        ${products.map((p) => `<td class="vt-compare__cell">${escapeHtml(p.vendor || '—')}</td>`).join('')}
      </tr>`;

    const typeRow = `
      <tr class="vt-compare__row">
        <th scope="row" class="vt-compare__label">Type</th>
        ${products.map((p) => `<td class="vt-compare__cell">${escapeHtml(p.type || '—')}</td>`).join('')}
      </tr>`;

    const availRow = `
      <tr class="vt-compare__row">
        <th scope="row" class="vt-compare__label">Availability</th>
        ${products.map((p) => `<td class="vt-compare__cell">${this._availabilityHtml(p)}</td>`).join('')}
      </tr>`;

    const atcRow = `
      <tr class="vt-compare__row vt-compare__row--action">
        <th scope="row" class="vt-compare__label">Add to cart</th>
        ${products.map((p) => `<td class="vt-compare__cell">${this._atcHtml(p)}</td>`).join('')}
      </tr>`;

    const removeRow = `
      <tr class="vt-compare__row vt-compare__row--action">
        <th scope="row" class="vt-compare__label">Remove</th>
        ${products.map((p) => `<td class="vt-compare__cell"><button type="button" class="vt-compare__remove" data-vt-remove="${escapeHtml(p.handle)}" aria-label="Remove ${escapeHtml(p.title)} from compare">${this._removeIcon()}</button></td>`).join('')}
      </tr>`;

    return [headRow, titleRow, priceRow, vendorRow, typeRow, availRow, atcRow, removeRow].join('');
  }

  _imageCell(p) {
    const img = featuredImage(p);
    const imgHtml = img
      ? `<img class="vt-compare__img" src="${escapeHtml(img)}" alt="${escapeHtml(p.title)}" loading="lazy" width="200" height="200">`
      : `<div class="vt-compare__img vt-compare__img--placeholder" aria-hidden="true">${this._placeholderSvg()}</div>`;
    return `<td class="vt-compare__cell vt-compare__cell--image"><a class="vt-compare__img-link" href="${escapeHtml(p.url || '/products/' + p.handle)}" aria-label="${escapeHtml(p.title)}">${imgHtml}</a></td>`;
  }

  _priceHtml(p) {
    const price = p.price || 0;
    const compareAt = p.compare_at_price || p.compare_at_price_max || 0;
    const onSale = compareAt > price;
    if (onSale) {
      return `<span class="vt-compare__price vt-compare__price--sale"><span class="vt-compare__price-now">${formatPrice(price)}</span> <s class="vt-compare__price-was">${formatPrice(compareAt)}</s></span>`;
    }
    return `<span class="vt-compare__price">${formatPrice(price)}</span>`;
  }

  _availabilityHtml(p) {
    if (p.available) {
      return `<span class="vt-compare__stock vt-compare__stock--in">In stock</span>`;
    }
    return `<span class="vt-compare__stock vt-compare__stock--out">Out of stock</span>`;
  }

  _atcHtml(p) {
    if (!p.available) {
      return `<button type="button" class="vt-compare__atc vt-compare__atc--disabled" disabled>Sold out</button>`;
    }
    const variantId = p.variants && p.variants.length ? p.variants[0].id : p.variant && p.variant[0] ? p.variant[0].id : p.id;
    return `<button type="button" class="vt-compare__atc" data-vt-atc="${escapeHtml(String(variantId))}" data-vt-handle="${escapeHtml(p.handle)}">Add to cart</button>`;
  }

  _removeIcon() {
    return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M3 6h18"></path><path d="M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"></path><path d="M10 11v6"></path><path d="M14 11v6"></path></svg>`;
  }

  _placeholderSvg() {
    return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.2" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="3" width="18" height="18" rx="2"></rect><circle cx="9" cy="9" r="2"></circle><path d="M21 15l-5-5L5 21"></path></svg>`;
  }

  /* ---------- table interactions ---------- */

  _wireTable() {
    // Remove buttons
    this.querySelectorAll('[data-vt-remove]').forEach((btn) => {
      btn.addEventListener('click', () => {
        const handle = btn.getAttribute('data-vt-remove');
        VTCompare.remove(handle);
      });
    });

    // Add-to-cart buttons
    this.querySelectorAll('[data-vt-atc]').forEach((btn) => {
      btn.addEventListener('click', async () => {
        if (btn.disabled) return;
        const id = btn.getAttribute('data-vt-atc');
        const original = btn.textContent;
        btn.disabled = true;
        btn.textContent = 'Adding…';
        try {
          const res = await fetch('/cart/add.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({ id: Number(id), quantity: 1 }),
          });
          if (!res.ok) throw new Error(`HTTP ${res.status}`);
          btn.textContent = 'Added';
          // Let the theme/cart drawer pick it up.
          document.dispatchEvent(new CustomEvent('cart:updated'));
          // Horizon / Dawn-style cart events:
          document.dispatchEvent(new CustomEvent('cart:item-added', { detail: await res.json().catch({}) }));
          setTimeout(() => {
            btn.disabled = false;
            btn.textContent = original;
          }, 1500);
        } catch (_err) {
          btn.textContent = 'Try again';
          setTimeout(() => {
            btn.disabled = false;
            btn.textContent = original;
          }, 1500);
        }
      });
    });
  }
}

/* ------------------------------------------------------------------ */
/* Register elements (guard against double registration)              */
/* ------------------------------------------------------------------ */

if (!customElements.get('vt-compare-button')) {
  customElements.define('vt-compare-button', VTCompareButton);
}
if (!customElements.get('vt-compare-page')) {
  customElements.define('vt-compare-page', VTComparePage);
}

/* ------------------------------------------------------------------ */
/* Module exports                                                     */
/* ------------------------------------------------------------------ */

export { VTCompareButton, VTComparePage };
export default VTCompare;
